import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Paper,
  Alert,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useAuth } from '../components/AuthContext.tsx';
import { authenticatedApiCall } from '../api.ts';

function ProfilePage() {
  const { user, token, logout } = useAuth();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    has_car: false,
    car_motorization: '',
    has_motorcycle: false,
    needs_pmr: false,
    personal_constraints: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email || '',
        password: '',
        confirmPassword: '',
        has_car: user.has_car,
        car_motorization: user.car_motorization || '',
        has_motorcycle: user.has_motorcycle,
        needs_pmr: user.needs_pmr,
        personal_constraints: user.personal_constraints || '',
      });
      setLoading(false);
    }
  }, [user]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (formData.password && formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    // Construire l'objet de données à envoyer
    const { confirmPassword, password, ...restOfData } = formData;
    const dataToSend: any = { ...restOfData };
    if (password) {
      dataToSend.password = password;
    }

    try {
      await authenticatedApiCall('PUT', '/users/me/', token, dataToSend);
      setSuccess('Profil mis à jour avec succès !');
      // Si l'email a été changé, l'utilisateur devrait se reconnecter
      if (user?.email !== dataToSend.email) {
        setTimeout(() => logout(), 2000);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la mise à jour du profil.');
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Container component={Paper} maxWidth="md" sx={{ p: 4, mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Mon Profil
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        <TextField name="first_name" label="Prénom" value={formData.first_name} onChange={handleChange} fullWidth margin="normal" required />
        <TextField name="last_name" label="Nom" value={formData.last_name} onChange={handleChange} fullWidth margin="normal" required />
        <TextField name="email" label="Adresse e-mail" type="email" value={formData.email} onChange={handleChange} fullWidth margin="normal" required />

        <Typography variant="h6" sx={{ mt: 3 }}>Véhicules</Typography>
        <FormGroup>
          <FormControlLabel control={<Checkbox name="has_car" checked={formData.has_car} onChange={handleChange} />} label="J'ai une voiture" />
          {formData.has_car && (
            <FormControl fullWidth margin="normal" sx={{ pl: 4 }}>
              <InputLabel>Motorisation</InputLabel>
              <Select name="car_motorization" value={formData.car_motorization} label="Motorisation" onChange={handleSelectChange}>
                <MenuItem value=""><em>Non spécifié</em></MenuItem>
                <MenuItem value="Thermique">Thermique</MenuItem>
                <MenuItem value="Hybride rechargeable">Hybride rechargeable</MenuItem>
                <MenuItem value="Electrique">Électrique</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControlLabel control={<Checkbox name="has_motorcycle" checked={formData.has_motorcycle} onChange={handleChange} />} label="J'ai une moto" />
        </FormGroup>

        <Typography variant="h6" sx={{ mt: 3 }}>Autres informations</Typography>
        <FormControlLabel control={<Checkbox name="needs_pmr" checked={formData.needs_pmr} onChange={handleChange} />} label="Besoin d'un accès PMR" />
        <TextField name="personal_constraints" label="Contraintes personnelles" value={formData.personal_constraints} onChange={handleChange} fullWidth margin="normal" multiline rows={2} />

        <Typography variant="h6" sx={{ mt: 3 }}>Changer le mot de passe</Typography>
        <TextField name="password" label="Nouveau mot de passe" type="password" value={formData.password} onChange={handleChange} fullWidth margin="normal" autoComplete="new-password" />
        <TextField name="confirmPassword" label="Confirmer le nouveau mot de passe" type="password" value={formData.confirmPassword} onChange={handleChange} fullWidth margin="normal" autoComplete="new-password" />

        <Button type="submit" variant="contained" color="primary" sx={{ mt: 3 }}>
          Mettre à jour le profil
        </Button>
      </Box>
    </Container>
  );
}

export default ProfilePage;
